import { ResponseTypeRequest } from '../../../@types/http-type';
import { CallHttp } from 'service/api/call/call-http-infra';

interface Request {
  data?: any;
  headers?: any;
  responseType?: ResponseTypeRequest;
}

export interface ServiceOperationResult<T> {
  success: boolean;
  message: string;
  statusCode: number;
  data: T | null;
  errors: string[];
  totalRegistros?: number;
}

export class RestUseCase {
  constructor(private readonly endPoint: string) {
    this.endPoint = endPoint;
  }

  async Get_<T>(request?: Request): Promise<ServiceOperationResult<T>> {
    try {
      const response = await CallHttp({
        url: this.endPoint,
        method: 'GET',
        headers: request?.headers,
        responseType: request?.responseType,
      });

      // Verifica se a resposta contém um erro e se há dados no erro
      const responseData = response.body ?? response.error?.data;

      // Mapeia os campos corretamente para ServiceOperationResult<T>
      const result: ServiceOperationResult<T> = {
        success: responseData?.success ?? false,
        message: responseData?.message ?? 'Ops!!! Algo deu errado',
        statusCode: responseData?.statusCode ?? response.statusCode ?? 500,
        data: responseData?.data ?? null,
        errors: responseData?.errors ?? [],
        totalRegistros: responseData?.totalRegistros ?? 0,
      };

      return result;
    } catch (error) {
      console.error('Erro na requisição GET_:', error);

      return {
        success: false,
        message: 'Erro ao realizar a requisição',
        statusCode: 500,
        data: null,
        errors: ['Erro inesperado ao buscar dados'],
      };
    }
  }

  async Delete_<T>(request?: Request): Promise<ServiceOperationResult<T>> {
    try {
      const response = await CallHttp({
        url: this.endPoint,
        method: 'DELETE',
        headers: request?.headers,
        body: request?.data,
      });

      // Verifica se a resposta contém um erro e se há dados no erro
      const responseData = response.body ?? response.error?.data;

      // Mapeia os campos corretamente para ServiceOperationResult<T>
      const result: ServiceOperationResult<T> = {
        success: responseData?.success ?? false,
        message: responseData?.message ?? 'Ops!!! Algo deu errado',
        statusCode: responseData?.statusCode ?? response.statusCode ?? 500,
        data: responseData?.data ?? null,
        errors: responseData?.errors ?? [],
        totalRegistros: responseData?.totalRegistros ?? 0,
      };

      return result;
    } catch (error) {
      console.error('Erro na requisição DELETE:', error);

      return {
        success: false,
        message: 'Erro ao realizar a requisição DELETE',
        statusCode: 500,
        data: null,
        errors: ['Erro inesperado ao excluir dados'],
      };
    }
  }

  async Post_<T>(request: Request): Promise<ServiceOperationResult<T>> {
    try {
      const response = await CallHttp({
        url: this.endPoint,
        method: 'POST',
        headers: request.headers,
        body: request.data,
      });

      console.info('Resposta da API:', response);

      // Verifica se a resposta contém um erro e se há dados no erro
      const responseData = response.body ?? response.error?.data;

      // Retorna os dados extraídos corretamente, seja de um sucesso ou de um erro
      const result: ServiceOperationResult<T> = {
        success: responseData?.success ?? false,
        message: responseData?.message ?? 'Ops!!! Algo deu errado',
        statusCode: responseData?.statusCode ?? response.statusCode ?? 500,
        data: responseData?.data ?? null,
        errors: responseData?.errors ?? [],
        totalRegistros: responseData?.totalRegistros ?? 0,
      };

      return result;
    } catch (error: any) {
      console.error('Erro na requisição POST:', error);
      const corpo = error?.response?.data ?? error?.data ?? null;

      return {
        success: false,
        message: corpo?.message ?? 'Erro ao realizar a requisição POST',
        statusCode: corpo?.statusCode ?? error?.response?.status ?? 500,
        data: null,
        errors: corpo?.errors ?? ['Erro inesperado ao enviar dados'],
      };
    }
  }

  async Get(request?: Request) {
    return await CallHttp({
      url: this.endPoint,
      method: 'GET',
      headers: request?.headers,
      responseType: request?.responseType,
    })
      .then((response) => {
        return {
          statusCode: response.statusCode,
          headers: response.headers,
          body: response.body,
          error: null,
        };
      })
      .catch((error) => {
        console.error('Erro na requisição GET:', error);

        return {
          success: false,
          message: 'Erro ao realizar a requisição GET',
          headers: error.header,
          statusCode: 500,
          body: error.data,
          errors: ['Erro inesperado ao enviar dados'],
        };
      });
  }

  async GetById(request?: Request) {
    return await CallHttp({
      url: this.endPoint,
      method: 'GET',
      headers: request?.headers,
    }).then((response) => response);
  }

  async Post(request?: Request) {
    return await CallHttp({
      url: this.endPoint,
      method: 'POST',
      body: request?.data,
      headers: request?.headers,
      responseType: request?.responseType,
    })
      .then((response) => {
        return {
          statusCode: response.statusCode,
          headers: response.headers,
          body: response.body,
          error: null,
        };
      })
      .catch((error) => {
        console.error('Erro na requisição POST:', error);

        return {
          success: false,
          message: 'Erro ao realizar a requisição POST',
          headers: error.header,
          statusCode: 500,
          body: error.data,
          errors: ['Erro inesperado ao enviar dados'],
        };
      });
  }

  async Put_<T>(request: Request): Promise<ServiceOperationResult<T>> {
    try {
      const response = await CallHttp({
        url: this.endPoint,
        method: 'PUT',
        headers: request.headers,
        body: request.data,
      });

      console.info('Resposta da API PUT_:', response);

      // Verifica se a resposta contém um erro e se há dados no erro
      const responseData = response.body ?? response.error?.data;

      // Retorna os dados extraídos corretamente, seja de um sucesso ou de um erro
      const result: ServiceOperationResult<T> = {
        success: responseData?.success ?? false,
        message: responseData?.message ?? 'Ops!!! Algo deu errado',
        statusCode: responseData?.statusCode ?? response.statusCode ?? 500,
        data: responseData?.data ?? null,
        errors: responseData?.errors ?? [],
        totalRegistros: responseData?.totalRegistros ?? 0,
      };

      return result;
    } catch (error) {
      console.error('Erro na requisição PUT_:', error);

      return {
        success: false,
        message: 'Erro ao realizar a requisição PUT',
        statusCode: 500,
        data: null,
        errors: ['Erro inesperado ao atualizar dados'],
      };
    }
  }

  async Put(request?: Request) {
    return await CallHttp({
      url: this.endPoint,
      method: 'PUT',
      body: request?.data,
      headers: request?.headers,
    }).then((response) => response);
  }

  async Patch(request?: Request) {
    return await CallHttp({
      url: this.endPoint,
      method: 'PATCH',
      body: request?.data,
      headers: request?.headers,
    }).then((response) => response);
  }

  async Delete(request?: Request) {
    return await CallHttp({
      url: this.endPoint,
      method: 'DELETE',
      body: request?.data,
      headers: request?.headers,
    }).then((response) => response);
  }
}
